import { isValidRegionSlug } from 'lib/region/regionLocale';

/**
 * Consent policy for the next analytics stack.
 *
 * Port of the locked zone table: each category turns on at the first moment
 * its mode allows, and a storefront can only tighten the visitor's IP zone.
 * The GTM container still contains ad tags, so GTM waits for ads until
 * `GTM_CONTAINER_CLEAN` is set.
 */

export const GTM_CONTAINER_CLEAN = false;

// Local midnight. India's date is computed from the DPDP Rules (G.S.R. 846(E)).
const INDIA_OPT_IN_MS = Date.parse('2027-05-13T00:00:00+05:30');
const SRI_LANKA_OPT_IN_MS = Date.parse('2027-01-01T00:00:00+05:30');

export type Mode = 'NOTICE' | 'OUT' | 'IN';
export type ZoneId = 'strict' | 'uk' | 'ch' | 'optin' | 'analyticsOut' | 'optout';

export type ConsentChoice = 'none' | 'accept' | 'reject' | 'analytics' | 'custom';

/** Switches the visitor set in Privacy Choices. Replay follows analytics. */
export type ConsentCategorySelection = {
  stats: boolean;
  analytics: boolean;
  ads: boolean;
};
export type PageTier = 'marketing' | 'health' | 'private';
export type Category = 'stats' | 'analytics' | 'replay' | 'ads' | 'attribution';

export type ZoneModes = { stats: Mode; analytics: Mode; ads: Mode };

export const ZONES: Record<ZoneId, ZoneModes> = {
  strict: { stats: 'IN', analytics: 'IN', ads: 'IN' },
  uk: { stats: 'OUT', analytics: 'IN', ads: 'IN' },
  ch: { stats: 'OUT', analytics: 'OUT', ads: 'IN' },
  optin: { stats: 'NOTICE', analytics: 'IN', ads: 'IN' },
  analyticsOut: { stats: 'NOTICE', analytics: 'OUT', ads: 'IN' },
  optout: { stats: 'NOTICE', analytics: 'OUT', ads: 'OUT' },
};

const RANK: Record<Mode, number> = { NOTICE: 0, OUT: 1, IN: 2 };

/**
 * Countries with an explicit zone. Anything else, including unknown, is strict.
 * Rows follow Legal's 28 Oct 2026 table. "Check the route" advertising rows sit
 * on analyticsOut until Legal confirms them; move a row to optout only then.
 * UAE statistics are not cleared for notice-only collection.
 */
export const COUNTRY_ZONES: Record<string, ZoneId> = {
  US: 'optin',
  GB: 'uk',
  CA: 'analyticsOut',
  CH: 'ch',
  NG: 'strict',
  AE: 'strict',
  AU: 'optout',
  MX: 'optout',
  // Advertising "Check the route".
  IN: 'analyticsOut',
  JP: 'analyticsOut',
  NZ: 'analyticsOut',
  HK: 'analyticsOut',
  TW: 'analyticsOut',
  PK: 'analyticsOut',
  LK: 'analyticsOut',
  // Ask first, or statistics need an assessment not yet on file.
  SG: 'optin',
  MY: 'optin',
  KR: 'optin',
  ZA: 'optin',
  QA: 'optin',
  BH: 'optin',
  SA: 'optin',
  TR: 'optin',
  IL: 'optin',
  BR: 'optin',
  AR: 'optin',
  CL: 'optin',
  CO: 'optin',
  PE: 'optin',
  KE: 'optin',
  VN: 'optin',
  TH: 'optin',
  PH: 'optin',
  ID: 'optin',
  CN: 'optin',
  BD: 'optin',
  EG: 'optin',
  OM: 'optin',
  JO: 'optin',
  KW: 'optin',
  AT: 'strict',
  BE: 'strict',
  BG: 'strict',
  HR: 'strict',
  CY: 'strict',
  CZ: 'strict',
  DK: 'strict',
  EE: 'strict',
  FI: 'strict',
  FR: 'strict',
  DE: 'strict',
  GR: 'strict',
  HU: 'strict',
  IE: 'strict',
  IT: 'strict',
  LV: 'strict',
  LT: 'strict',
  LU: 'strict',
  MT: 'strict',
  NL: 'strict',
  PL: 'strict',
  PT: 'strict',
  RO: 'strict',
  SK: 'strict',
  SI: 'strict',
  ES: 'strict',
  SE: 'strict',
  NO: 'strict',
  IS: 'strict',
  LI: 'strict',
};

export const POLICY_COUNTRIES: readonly string[] = Object.keys(COUNTRY_ZONES);

const PRIVATE_PREFIXES = [
  '/cycle-report',
  '/forms/metabolic-health-assessment',
  '/order',
  '/emerald-sphere',
  // Order, claim and redeem pages carry order ids, codes and contact details.
  '/ring/order/success',
  '/ring/claim',
  '/ring-pro/claim',
  '/gift-card/claim',
  '/gift-card/success',
  '/x5i28rjjuhg1/success',
  '/x/redeem',
  '/powerplugs/redeem',
  '/performance-lab/gift/checkout',
  '/blood-vision/buy/checkout',
  '/blood-vision/buy/failed',
  '/auth',
  '/join_group',
];

/** Public Blood Vision pages; any other `/blood-vision/<id>` is a partner code page. */
const BLOOD_VISION_PUBLIC = new Set(['buy', 'faq', 'tests', 'serviceability-check']);

/**
 * Public pages whose visit reveals interest in a specific condition, test or
 * treatment (per-page review, 2 Nov 2026). Ads and attribution stay off even
 * after consent until Legal clears them; analytics follows consent.
 */
const HEALTH_PREFIXES = [
  '/luminary',
  '/womens-health',
  '/ultrahuman-x-clue',
  '/ring/buy/better-help',
  '/performance-lab/glp',
  '/performance-lab/iv-lounge',
  '/blood-vision/buy',
  '/science/explainers/category/women',
];
const HEALTH_FLOWS = new Set(['luminary', 'clue', 'respiratory']);
/** Partner landings whose code names a fertility product. */
const HEALTH_AFFILIATES = new Set(['ovusense']);
const HEALTH_SCIENCE =
  /^\/science\/(explainers|studies|ask|accuracy)\/[^/]*\b(cycle|ovulat\w*|fertil\w*|pregnan\w*|menstrua\w*|miscarr\w*|embryo\w*|period|hot-flash\w*|pcos|afib|arrhythm\w*|glp|diabet\w*|mental-health)\b/;
const HEALTH_PL_PLAN = /^(iv-|pl-im-)|genome-health|gut-microbiome/;

export type EffectiveConsent = Record<Category, boolean>;

export type ConsentDecision = {
  ipZone: ZoneId;
  storefrontZone: ZoneId | null;
  modes: ZoneModes;
  effective: EffectiveConsent;
  page: PageTier;
};

export type DecideInput = {
  country: string | null | undefined;
  province?: string | null;
  /** Storefront country (US, CA, GB, …). Null skips the tighten step. */
  storefrontCountry?: string | null;
  choice?: ConsentChoice;
  /**
   * Explicit switches. They replace Accept / Reject presets. Global Privacy
   * Control still forces ads off, and a private page still loads nothing.
   * Notice-only statistics stay on only when this map is absent.
   */
  categories?: ConsentCategorySelection | null;
  /** Modes when `categories` was saved. A yes for a category that was not opt-in then is not consent now. */
  savedModes?: ZoneModes | null;
  gpc?: boolean;
  page?: PageTier;
  now?: Date;
  /** Kept for tests. Live traffic always leaves this on. */
  tripleWhale?: boolean;
};

function stricter(a: Mode, b: Mode): Mode {
  return RANK[a] >= RANK[b] ? a : b;
}

/**
 * A Canada IP with no province is treated as Quebec. `XX` is the server's
 * missing-header placeholder.
 */
export function isQuebec(
  country: string | null | undefined,
  province: string | null | undefined,
): boolean {
  if ((country ?? '').toUpperCase() !== 'CA') return false;
  if (province == null) return true;
  const value = province.trim().toUpperCase();
  if (value === '' || value === 'XX') return true;
  return value === 'QC';
}

export function zoneFor(
  country: string | null | undefined,
  now: Date,
  quebec: boolean,
): ZoneId {
  const code = (country ?? '').toUpperCase();
  if (!code) return 'strict';
  if (code === 'CA' && quebec) return 'strict';
  if (code === 'IN' && now.getTime() >= INDIA_OPT_IN_MS) return 'optin';
  if (code === 'LK' && now.getTime() >= SRI_LANKA_OPT_IN_MS) return 'optin';
  return COUNTRY_ZONES[code] ?? 'strict';
}

function normalizePath(pathname: string): string {
  const bare = pathname.split('?')[0]?.split('#')[0] ?? '/';
  const withSlash = bare.startsWith('/') ? bare : `/${bare}`;
  if (withSlash.length > 1 && withSlash.endsWith('/')) {
    return withSlash.slice(0, -1);
  }
  return withSlash || '/';
}

function stripRegion(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  const first = segments[0]?.toLowerCase();
  if (first && isValidRegionSlug(first)) {
    const rest = segments.slice(1).join('/');
    return rest ? `/${rest}` : '/';
  }
  return pathname;
}

function matchesPrefix(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

export function classifyRoute(pathname: string, search = ''): PageTier {
  const path = normalizePath(stripRegion(normalizePath(pathname)));
  if (PRIVATE_PREFIXES.some((prefix) => matchesPrefix(path, prefix))) {
    return 'private';
  }
  const [, first, second] = path.split('/');
  if (first === 'blood-vision' && second && !BLOOD_VISION_PUBLIC.has(second)) {
    return 'private';
  }
  if (
    HEALTH_PREFIXES.some((prefix) => matchesPrefix(path, prefix)) ||
    HEALTH_SCIENCE.test(path)
  ) {
    return 'health';
  }
  const params = new URLSearchParams(search);
  if (HEALTH_FLOWS.has(params.get('flow') ?? '')) return 'health';
  if (HEALTH_AFFILIATES.has((params.get('affiliateCode') ?? '').toLowerCase())) {
    return 'health';
  }
  if (
    path === '/performance-lab/buy' &&
    (params.get('category') === 'iv-therapy' ||
      HEALTH_PL_PLAN.test(params.get('plan') ?? ''))
  ) {
    return 'health';
  }
  return 'marketing';
}

export function isGtmAllowed(
  effective: Pick<EffectiveConsent, 'analytics' | 'ads'>,
  containerClean: boolean = GTM_CONTAINER_CLEAN,
): boolean {
  return effective.analytics && (effective.ads || containerClean);
}

export function decide(input: DecideInput): ConsentDecision {
  const now = input.now ?? new Date();
  const country = input.country ?? '';
  const choice = input.choice ?? 'none';
  const gpc = input.gpc ?? false;
  const page = input.page ?? 'marketing';
  const tripleWhale = input.tripleWhale ?? true;
  const quebec = isQuebec(country, input.province);

  const ipZone = zoneFor(country, now, quebec);
  const storeCode = input.storefrontCountry?.toUpperCase() || null;
  const storefrontZone = storeCode ? zoneFor(storeCode, now, false) : null;

  const ipModes = ZONES[ipZone];
  const modes: ZoneModes = storefrontZone
    ? {
        stats: stricter(ipModes.stats, ZONES[storefrontZone].stats),
        analytics: stricter(ipModes.analytics, ZONES[storefrontZone].analytics),
        ads: stricter(ipModes.ads, ZONES[storefrontZone].ads),
      }
    : ipModes;

  const explicit =
    choice === 'accept'
      ? { stats: true, analytics: true, ads: true, attribution: true }
      : choice === 'reject'
        ? { stats: false, analytics: false, ads: false, attribution: false }
        : choice === 'analytics'
          ? { stats: true, analytics: true, ads: false, attribution: false }
          : null;

  const effective: EffectiveConsent = {
    stats: false,
    analytics: false,
    replay: false,
    ads: false,
    attribution: false,
  };

  if (page === 'private') {
    return { ipZone, storefrontZone, modes, effective, page };
  }
  // Health pages never send to ad platforms, whatever the choice.
  const done = (): ConsentDecision => {
    if (page === 'health') {
      effective.ads = false;
      effective.attribution = false;
    }
    return { ipZone, storefrontZone, modes, effective, page };
  };

  if (input.categories) {
    const { categories } = input;
    const askedFor = (key: keyof ZoneModes) => input.savedModes?.[key] === 'IN';
    const kept = (key: keyof ZoneModes) =>
      categories[key] && (modes[key] !== 'IN' || askedFor(key));
    effective.stats = kept('stats');
    effective.analytics = kept('analytics');
    effective.ads = !gpc && kept('ads');
    // Replay and attribution always need a switch the visitor turned on.
    effective.replay = effective.analytics && askedFor('analytics');
    effective.attribution = effective.ads && tripleWhale && askedFor('ads');
    return done();
  }

  // Decline all is an objection to notice-only statistics too.
  if (explicit) {
    effective.stats = explicit.stats;
  } else if (modes.stats !== 'IN') {
    effective.stats = true;
  }

  if (explicit) {
    effective.analytics = explicit.analytics;
  } else if (modes.analytics === 'OUT' && !gpc) {
    effective.analytics = true;
  }

  if (gpc) {
    effective.ads = false;
  } else if (explicit) {
    effective.ads = explicit.ads;
  } else if (modes.ads === 'OUT') {
    effective.ads = true;
  }

  if (explicit?.analytics) {
    effective.replay = true;
  }

  if (!gpc && tripleWhale && explicit?.attribution) {
    effective.attribution = true;
  }

  return done();
}

/**
 * Opt-out categories stay off until the banner is on screen, unless the
 * visitor already made a choice. Notice-only and explicit grants do not wait.
 */
export function releaseOptOut(
  mode: Mode,
  allowed: boolean,
  choice: ConsentChoice,
  bannerReady: boolean,
): boolean {
  if (!allowed) return false;
  if (mode === 'OUT' && choice === 'none' && !bannerReady) return false;
  return true;
}
